import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DashboardLayout from "../../../components/dashboard/DashboardLayout";
import {
  ConfirmDialog,
  ErrorState,
  LoadingState,
} from "../../../components/dashboard/DashboardStates";
import { useToast } from "../../../context/ToastContext";
import * as fieldsApi from "../../../api/fieldsApi";
import * as mediaApi from "../../../api/mediaApi";
import * as pagesApi from "../../../api/pagesApi";
import * as sectionsApi from "../../../api/sectionsApi";
import { getApiErrorMessage } from "../../../utils/apiResponse";
import DynamicFieldsEditor from "../fields/DynamicFieldsEditor";
import MediaManager, { sectionSupportsMedia } from "../media/MediaManager";

const SectionEditor = () => {
  const { sectionId } = useParams();
  const toast = useToast();
  const [section, setSection] = useState(null);
  const [fields, setFields] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyFieldId, setBusyFieldId] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [deletingMedia, setDeletingMedia] = useState(null);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const [fieldData, mediaData, pages] = await Promise.all([
          fieldsApi.getFieldsBySection(sectionId),
          mediaApi.getMediaBySection(sectionId),
          pagesApi.getPages(1, 100),
        ]);

        setFields(fieldData || []);
        setMedia(mediaData || []);

        let found = null;
        for (const page of pages?.items || []) {
          const secs = await sectionsApi.getSectionsByPage(page.id);
          found = (secs || []).find((s) => String(s.id) === String(sectionId));
          if (found) break;
        }

        if (!found) {
          throw new Error("Section not found.");
        }

        setSection(found);
      } catch (err) {
        setError(getApiErrorMessage(err));
        setSection(null);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [sectionId]);

  const handleSaveField = async (field, translations) => {
    setBusyFieldId(field.id);
    try {
      await fieldsApi.updateField(field.id, {
        label: field.label,
        fieldType: field.fieldType,
        isRequired: field.isRequired,
        isPublished: field.isPublished,
        sortOrder: field.sortOrder,
        translations,
        rowVersion: field.rowVersion,
      });
      toast.success(`Saved “${field.label}”.`);
      const fieldData = await fieldsApi.getFieldsBySection(sectionId);
      setFields(fieldData || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusyFieldId(null);
    }
  };

  const handleUpload = async ({ file, groupKey }) => {
    setUploadBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("groupKey", groupKey || "main");
      await mediaApi.uploadMedia(sectionId, formData);
      toast.success("Image uploaded.");
      setMedia((await mediaApi.getMediaBySection(sectionId)) || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setUploadBusy(false);
    }
  };

  const handleReplace = async (item, file) => {
    setUploadBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const updated = await mediaApi.replaceMedia(item.id, formData);
      toast.success("Image replaced.");
      setMedia((prev) =>
        (prev || []).map((m) => (m.id === item.id ? { ...m, ...updated } : m))
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err));
      setMedia((await mediaApi.getMediaBySection(sectionId)) || []);
    } finally {
      setUploadBusy(false);
    }
  };

  const handleDeleteMedia = async () => {
    if (!deletingMedia) return;
    setUploadBusy(true);
    try {
      await mediaApi.deleteMedia(deletingMedia.id);
      toast.success("Media deleted.");
      setDeletingMedia(null);
      setMedia((await mediaApi.getMediaBySection(sectionId)) || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setUploadBusy(false);
    }
  };

  return (
    <DashboardLayout
      title={section ? section.name : "Section editor"}
      subtitle={
        section
          ? `Key: ${section.key} · Type: ${section.sectionType}`
          : "Edit fields and contextual media."
      }
    >
      {section?.cmsPageId ? (
        <Link
          to={`/dashboard/pages/${section.cmsPageId}/sections`}
          className="mb-5 inline-block text-sm font-medium text-emerald-700 hover:underline"
        >
          ← Back to sections
        </Link>
      ) : (
        <Link
          to="/dashboard/pages"
          className="mb-5 inline-block text-sm font-medium text-emerald-700 hover:underline"
        >
          ← Back to pages
        </Link>
      )}

      {loading ? <LoadingState label="Loading section content…" /> : null}
      {!loading && error ? <ErrorState message={error} /> : null}

      {!loading && !error && section ? (
        <div className="grid gap-6 xl:grid-cols-[1.4fr_1fr]">
          <section>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
              Fields
            </h3>
            {fields.length === 0 ? (
              <p className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
                No fields on this section yet.
              </p>
            ) : (
              <DynamicFieldsEditor
                key={fields.map((f) => `${f.id}-${f.rowVersion}`).join(",")}
                fields={fields}
                onSaveField={handleSaveField}
                busyFieldId={busyFieldId}
              />
            )}
          </section>

          <section>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
              Media
            </h3>
            {sectionSupportsMedia(section.sectionType) ? (
              <MediaManager
                mediaItems={media}
                onUpload={handleUpload}
                onReplace={handleReplace}
                onDelete={setDeletingMedia}
                busy={uploadBusy}
              />
            ) : (
              <p className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
                This section type does not expose media actions.
              </p>
            )}
          </section>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(deletingMedia)}
        title="Delete media"
        description="Remove this media record from the section?"
        busy={uploadBusy}
        onCancel={() => setDeletingMedia(null)}
        onConfirm={handleDeleteMedia}
      />
    </DashboardLayout>
  );
};

export default SectionEditor;
